import type {
  FeedProductInterface,
  FormattedFeedProductType,
  OfferPriceType,
} from '@/types'

const hasSamePriceValues = (first: OfferPriceType, second: OfferPriceType) =>
  first.sellingPrice === second.sellingPrice &&
  first.retailPrice === second.retailPrice &&
  first.purchasePrice === second.purchasePrice &&
  first.unavailableReason === second.unavailableReason

const getStockOffers = (product: FeedProductInterface) =>
  product.suppliersStock.flatMap((supplierStockInfo) =>
    supplierStockInfo.stock.map((storeInfo) => ({
      ...product,
      suppliersStock: [{ ...supplierStockInfo, stock: [storeInfo] }],
    }))
  )

const getPriceOffers = (product: FeedProductInterface) =>
  product.suppliersStock
    .flatMap((supplier) =>
      supplier.prices.map((price) => ({ supplier, price }))
    )
    .filter(
      (offer, index, offers) =>
        offers.findIndex((other) =>
          hasSamePriceValues(other.price, offer.price)
        ) === index
    )
    .map(({ supplier, price }) => ({
      ...product,
      suppliersStock: [{ ...supplier, prices: [price], stock: [] }],
    }))

const getOffers = (product: FeedProductInterface) => {
  const hasStock = product.suppliersStock.some(
    (supplier) => supplier.stock.length > 0
  )
  return hasStock ? getStockOffers(product) : getPriceOffers(product)
}

export default function feedProductsFormatter(
  products: FeedProductInterface[]
): FormattedFeedProductType[] {
  return products.map((product): FormattedFeedProductType => {
    const allOffersForProduct = getOffers(product)

    if (allOffersForProduct.length === 0) {
      return { parent: true, ...product }
    }

    if (allOffersForProduct.length === 1) {
      return { ...allOffersForProduct[0], parent: true }
    }

    const [firstOffer, ...rest] = allOffersForProduct

    const children = rest.map((childOffer, index, arr) => ({
      ...childOffer,
      lastInChildren: index === arr.length - 1,
      parent: false,
    }))

    return { ...firstOffer, parent: true, children }
  })
}
